<template>
  <div class="uber-details-outer">
    <!-- Same close affordance the standard TripDetails page uses. -->
    <div class="close-blk" v-if="closable" @click="close">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
        <path d="m15 9-6 6" />
        <path d="m9 9 6 6" />
      </svg>
    </div>

    <div class="empty-state" v-if="!tripId">
      <i class="fas fa-taxi"></i>
      <span>Select a trip to see its details</span>
    </div>

    <div class="empty-state" v-else-if="!trip && !loading">
      <i class="fas fa-exclamation-circle"></i>
      <span>Could not load this trip.</span>
    </div>

    <div class="trip-details-modal" v-else-if="trip">
      <!-- Status timeline, oldest to newest, in place of TripTimeLine -->
      <div class="timeline-outer">
        <button @click="scrollLeft" class="scroll-btn left-btn" v-if="isAtStart">
          <i class="fas fa-angle-left"></i>
        </button>
        <div class="timeline-container" ref="timelineContainer" @scroll="showScroll">
        <div class="timeline-content" v-if="timeline.length > 0">
          <div v-for="(event, index) in timeline" :key="index" class="timeline-item">
            <div
              class="timeline-dot"
              :class="{ current: index === timeline.length - 1, superseded: event.superseded }"
            ></div>
            <!-- Line order matches TripTimeLine on the standard details page:
                 timestamp, then what happened, then who. -->
            <div class="timeline-label">
              <div class="timeline-date">{{ event.time }}</div>
              <div class="timeline-status">{{ event.status }}</div>
              <div class="timeline-action" v-if="event.message">{{ event.message }}</div>
              <!-- Who caused it: the admin for a portal action, "Uber" for a webhook. -->
              <div class="timeline-by">{{ event.employee }}</div>
              <!-- Only trip-log entries carry coordinates. -->
              <div class="timeline-action" v-if="event.location">
                <a
                  :href="`https://maps.google.com/?q=${event.location.lat},${event.location.lng}`"
                  target="_blank"
                  rel="noopener"
                  @click.stop
                >
                  View location
                </a>
              </div>
            </div>
          </div>
        </div>
          <div class="timeline-empty" v-else>No status history yet.</div>
        </div>
        <button @click="scrollRight" class="scroll-btn right-btn" v-if="isAtEnd">
          <i class="fas fa-angle-right"></i>
        </button>
      </div>

      <v-card elevation="3" class="trip-details-card-modal mt-10">
        <div class="trip-id-column details-column">
          <span class="header-blk"># ID</span>
          <strong>{{ trip.displayId }}</strong>
          <!-- Only when the trip has a separate human-facing id. -->
          <b v-if="trip.displayId !== trip.tripId">{{ trip.tripId }}</b>
        </div>
        <div class="user-column details-column">
          <span class="header-blk">{{ !$isIndigo ? "Customer" : "Crew Name" }}</span>
          <strong>{{ guestName }}</strong>
        </div>
        <div class="number-column details-column">
          <span class="header-blk">Number</span>
          <strong>{{ (trip.guest && trip.guest.phoneNumber) || "-" }}</strong>
        </div>
        <!-- Keeps the first row at 100%; without it the grid leaves a gap. -->
        <div class="cab-type-column details-column">
          <span class="header-blk">Service</span>
          <span>{{ serviceTypeLabel }}</span>
        </div>
        <div class="trip-type-column details-column">
          <span class="header-blk">Booking Type</span>
          <span>{{ tripTypeLabel }}</span>
        </div>
        <!-- One column, not two: the card grid uses fixed percentages that must
             total 100%. It shows whichever status is meaningful for this trip. -->
        <div class="trip-status-column details-column">
          <span class="header-blk">{{ statusColumn.label }}</span>
          <span :style="{ color: statusColumn.color }">{{ statusColumn.value }}</span>
        </div>
        <div class="date-column details-column">
          <span class="header-blk">Trip Date</span>
          <span>{{ getTimeDate(trip.pickupTime) }}</span>
        </div>
        <div class="reporting-column details-column">
          <span class="header-blk">Reporting Time</span>
          <span>{{ getTimeDate(trip.reportingTime) }}</span>
        </div>
        <div class="from-location-column details-column">
          <span class="header-blk">{{ !$isIndigo ? "From" : "Pickup Address" }}</span>
          <span>{{ addressOf(trip.pickup) }}</span>
        </div>
        <div class="to-location-column details-column">
          <span class="header-blk">{{ !$isIndigo ? "To" : "Drop Address" }}</span>
          <span>{{ addressOf(trip.dropoff) }}</span>
        </div>
      </v-card>

      <!-- Always shown, so the drawer has the same shape whether or not the trip
           is booked. Before a booking the fields simply read "-". -->
      <v-card elevation="3" class="customer-details-modal">
        <div class="branch-column">
          <span class="user-header-blk">Uber Trip ID</span>
          <span>{{ trip.uberTripId || "-" }}</span>
        </div>
        <div class="branch-column">
          <span class="user-header-blk">Driver</span>
          <!-- The call lives in the action panel below, as a labelled button. A
               bare phone icon here did not say who would be called or how.

               The number is commented out of the name line - Call Driver places
               the call, so it was never needed to act on. -->
          <span v-if="driverDetailsVisible && driver.name">{{ driver.name }}<!-- - {{ driver.mobile || driver.phoneNumber || "-" }} --></span>
          <span v-else>-</span>
        </div>
        <div class="branch-column">
          <span class="user-header-blk">Car</span>
          <span v-if="driverDetailsVisible && vehicleName !== '-'">{{ vehicle.licensePlate || vehicle.vehicleno || "-" }} - {{ vehicleName }}</span>
          <span v-else>-</span>
        </div>
        <div class="branch-column" v-if="driverDetailsVisible && driver.rating">
          <span class="user-header-blk">Driver Rating</span>
          <span>{{ driver.rating }}</span>
        </div>
        <div class="branch-column">
          <span class="user-header-blk">Booking Time</span>
          <span>{{ getTimeDate(trip.createdAt) }}</span>
        </div>
        <!-- What Uber finally charged, off the receipt rather than the estimate.
             Gated on the figure existing rather than on the trip's status, which
             is what makes it cover a cancellation that carried a fee as well as
             a completed ride. -->
        <div class="branch-column" v-if="finalFareTotal">
          <span class="user-header-blk">{{ finalFareLabel }}</span>
          <strong>{{ finalFareTotal }}</strong>
        </div>
        <!-- Dropped once the ride is over: a PIN and an ETA describe a car that
             is still coming, and Uber's tracking link stops working anyway. -->
        <template v-if="ridePending">
          <!-- Only once a driver is assigned: before that there is nobody to
               read it to, and it just adds a number to an already dense card. -->
          <div class="branch-column" v-if="showPin">
            <span class="user-header-blk">OTP</span>
            <!-- What the crew gives the driver at pickup. Uber derives it from
                 the passenger's number, so it is read off the booking rather
                 than stored. -->
            <strong class="ride-pin">{{ ridePin }}</strong>
          </div>
          <!-- Clock times, computed on the server from its own clock - see
               etaClock(). The browser never turns minutes into a time. -->
          <div class="branch-column">
            <span class="user-header-blk">Pickup By</span>
            <span>{{ trip.pickupEtaAt || "-" }}</span>
          </div>
          <div class="branch-column">
            <span class="user-header-blk">Drop By</span>
            <span>{{ trip.dropEtaAt || "-" }}</span>
          </div>
        </template>
        <div class="branch-column" v-if="trip.reBooking">
          <span class="user-header-blk">Re-booked</span>
          <span>From an earlier trip</span>
        </div>
      </v-card>

      <!--
        Action panel, in the shape of the corporate portal's Agent Support
        section: each action states what it does before it is pressed, rather
        than being an unlabelled icon or a bare verb.

        Every card carries its own status gate, so only what this trip's state
        actually allows is offered.
      -->
      <v-card elevation="3" class="action-panel" v-if="hasActions">
        <div class="action-panel-title">Actions</div>
        <div class="action-grid">
          <div class="action-card" v-if="trackable">
            <v-icon size="20" color="#1565c0">mdi-map-marker-path</v-icon>
            <div class="action-card-title">Live Tracking</div>
            <div class="action-card-sub">
            </div>
            <v-btn small dark depressed color="#1565c0" class="action-card-btn" @click="openTracking">
              Track Ride
            </v-btn>
          </div>

          <!--
            User Link - hidden for now.

            The page itself, its route and its endpoints are all still in place;
            this only takes the way in off the drawer. Restore by uncommenting
            this block and the `shareable ||` line in hasActions below.
          -->
          <!--
          <div class="action-card" v-if="shareable">
            <v-icon size="20" color="#0f172a">mdi-eye-outline</v-icon>
            <div class="action-card-title">User Link</div>
            <div class="action-card-sub">
            </div>
            <div class="action-card-pair">
              <v-btn small dark depressed color="#0f172a" class="action-card-btn" @click="openUserLink">
                Open
              </v-btn>
              <v-btn small outlined class="action-card-btn" @click="copyUserLink">
                {{ linkCopied ? "Copied" : "Copy" }}
              </v-btn>
            </div>
          </div>
          -->

          <div class="action-card" v-if="callable">
            <v-icon size="20" color="#0f766e">mdi-phone</v-icon>
            <div class="action-card-title">Call Driver</div>
            <div class="action-card-sub">
            </div>
            <v-btn
              small
              dark
              depressed
              color="#0f766e"
              class="action-card-btn"
              :loading="calling"
              @click="callDriver"
            >
              Call Driver
            </v-btn>
          </div>

          <div class="action-card" v-if="messageable">
            <v-icon size="20" color="#4f46e5">mdi-message-text-outline</v-icon>
            <div class="action-card-title">Message Driver</div>
            <div class="action-card-sub">
            </div>
            <v-btn small dark depressed color="#4f46e5" class="action-card-btn" @click="messagesDialog = true">
              Open Chat
            </v-btn>
          </div>

          <!-- Shown only once the receipt_ready webhook has landed, so the
               button is never offered for a receipt Uber has not issued. The
               card appearing at all is what says the receipt is ready; the
               button says what pressing it does. Offered after a cancellation
               too - a cancellation fee is a charge worth seeing. -->
          <div class="action-card" v-if="receiptable">
            <v-icon size="20" color="#374151">mdi-receipt-text-outline</v-icon>
            <div class="action-card-title">Trip Receipt</div>
            <div class="action-card-sub">
            </div>
            <v-btn small dark depressed color="#374151" class="action-card-btn"
                   :loading="loadingReceipt" @click="openReceipt">
              View Receipt
            </v-btn>
          </div>

          <div class="action-card" v-if="reportable">
            <v-icon size="20" color="#b45309">mdi-clipboard-alert-outline</v-icon>
            <div class="action-card-title">Report Issue</div>
            <div class="action-card-sub">
              Record a complaint about this trip for follow-up.
            </div>
            <v-btn small dark depressed color="#b45309" class="action-card-btn" @click="openIssue('report')">
              Report Issue
            </v-btn>
          </div>

          <div class="action-card" v-if="reportable">
            <v-icon size="20" color="#b91c1c">mdi-shield-alert-outline</v-icon>
            <div class="action-card-title">Safety Concern</div>
            <div class="action-card-sub">
              Escalate anything that put the crew at risk.
            </div>
            <v-btn small dark depressed color="#b91c1c" class="action-card-btn" @click="openIssue('safety')">
              Escalate
            </v-btn>
          </div>

          <div class="action-card" v-if="cancellable">
            <v-icon size="20" color="#c62828">mdi-close-circle-outline</v-icon>
            <div class="action-card-title">Cancel Ride</div>
            <div class="action-card-sub">
            </div>
            <v-btn
              small
              dark
              depressed
              color="#c62828"
              class="action-card-btn"
              :loading="cancelling"
              @click="cancelTrip"
            >
              Cancel Ride
            </v-btn>
          </div>
        </div>
      </v-card>
    </div>

    <!-- ------------------------------------------------------------------ -->
    <!-- Report an issue / escalate a safety concern                        -->
    <!-- One dialog for both: the only difference is the issue type, which a -->
    <!-- safety concern does not choose.                                     -->
    <!-- ------------------------------------------------------------------ -->
    <!--
      The receipt, as Uber gives it: charge lines, a total, and how far and how
      long the ride was. Uber returns this as JSON rather than a document, so
      there is nothing to open in a tab - Print is what turns it into a PDF.
    -->
    <v-dialog v-model="receiptDialog" width="460">
      <v-card class="issue-card">
        <!-- The total leads, the way a receipt does - what was charged is the
             thing being looked up, and the breakdown is the justification. -->
        <div class="receipt-hero">
          <div class="receipt-hero-label">Total charged</div>
          <div class="receipt-hero-total">{{ receiptTotal }}</div>
          <div class="receipt-hero-sub">
            Trip {{ trip.displayId || trip.tripId }}<span v-if="receiptDate"> · {{ receiptDate }}</span>
          </div>
        </div>

        <div class="receipt-body">
          <table class="receipt-table" v-if="receiptLines.length">
            <template v-for="(line, index) in receiptLines">
              <!-- Sits above the first adjustment, so waiting time and tolls
                   read as additions rather than part of the fare. -->
              <tr v-if="line.heading" :key="'h' + index" class="receipt-group-row">
                <td colspan="2">{{ line.heading }}</td>
              </tr>
              <tr :key="index" :class="{ 'receipt-spaced-row': line.spaced }">
                <td>{{ line.name }}</td>
                <td class="receipt-amount">{{ lineAmount(line.amount) }}</td>
              </tr>
            </template>
            <tr class="receipt-total-row">
              <td>Total</td>
              <td class="receipt-amount">{{ receiptTotal }}</td>
            </tr>
          </table>
          <p class="receipt-empty" v-else>
            Uber charged this ride as a single fare, with no itemised breakdown.
          </p>

          <!-- The same list the PDF draws from, so the two cannot drift. -->
          <div class="receipt-section">Trip details</div>
          <table class="receipt-facts">
            <tr v-for="fact in receiptFacts" :key="fact.key">
              <td class="receipt-fact-key">{{ fact.key }}</td>
              <td :class="{ 'receipt-uber-id': fact.key === 'Uber trip' }">{{ fact.value }}</td>
            </tr>
          </table>
        </div>

        <div class="issue-actions">
          <v-btn small dark depressed color="#374151" @click="downloadReceipt">
            <v-icon left small>mdi-download</v-icon>
            Download PDF
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text small @click="receiptDialog = false">Close</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="issueDialog" width="560" persistent>
      <v-card class="issue-card">
        <div class="issue-header" :class="issueType === 'safety' ? 'safety' : ''">
          <i :class="issueType === 'safety' ? 'fas fa-shield-alt' : 'fas fa-clipboard-list'"></i>
          {{ issueType === "safety" ? "Escalate a Safety Concern" : "Report an Issue" }}
        </div>

        <div class="issue-body">
          <p class="issue-note" v-if="issueType === 'safety'">
            This is recorded against the trip and escalated by email. Use it for
            anything that put the crew at risk.
          </p>
          <p class="issue-note" v-else>
            Recorded against this trip for follow-up. Nothing is sent to Uber.
          </p>

          <v-select
            v-if="issueType !== 'safety'"
            v-model="issueForm.issueType"
            :items="issueTypeOptions"
            item-text="label"
            item-value="value"
            label="Issue type"
            color="#ff9600"
            outlined
            dense
            hide-details
            class="mb-3"
          ></v-select>

          <v-textarea
            v-model="issueForm.description"
            label="What happened?"
            rows="4"
            outlined
            dense
            hide-details
            color="#ff9600"
            counter="2000"
          ></v-textarea>
        </div>

        <div class="issue-actions">
          <v-btn text small @click="issueDialog = false">Close</v-btn>
          <v-btn
            small
            :color="issueType === 'safety' ? '#b91c1c' : '#b45309'"
            class="issue-submit"
            :loading="submittingIssue"
            :disabled="!canSubmitIssue"
            @click="submitIssue"
          >
            {{ issueType === "safety" ? "Escalate" : "Report" }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <DriverMessages
      v-model="messagesDialog"
      :tripId="tripId"
      :driverName="driver.name || 'Driver'"
    />

    <Loading v-if="loading"></Loading>
    <Confirm ref="confirm"></Confirm>
  </div>
</template>

<script>
import * as moment from "moment";
import axios from "@/axios/axios-global";
import toast from "@/components/common/toast";
import { readApiError } from "./apiError";
import Loading from "@/components/common/Loading";
import Confirm from "./UberConfirm.vue";
import uberRealtime from "@/services/uberRealtime";
import DriverMessages from "./DriverMessages.vue";
// Drawn in the browser, so the API server needs no PDF engine. Uber supplies
// the receipt as figures only - there is no document of theirs to hand over.
import { jsPDF } from "jspdf";
import {
  TRIP_TYPE_OPTIONS,
  statusLabel,
  statusColor,
  isCancellable,
  canCallDriver,
  canMessageDriver,
  canReportIssue,
  hasDriverAssigned,
  hasLiveTracking,
  hasReceipt,
  shouldShowDriverDetails,
  statusDescriptor,
  STATUS_GROUP,
  ISSUE_TYPES,
} from "@/config/uberStatus";

export default {
  name: "UberTripDetails",
  components: { Loading, Confirm, DriverMessages },
  props: {
    tripId: {
      type: [Number, String],
      default: 0,
    },
    // Set when rendered inside a drawer, so it can offer a way out. The same
    // component is reused without it wherever it sits inline.
    closable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      trip: null,
      loading: false,
      cancelling: false,
      calling: false,
      loadingReceipt: false,
      receiptDialog: false,
      receipt: null,
      unsubscribe: null,
      messagesDialog: false,
      linkCopied: false,
      issueDialog: false,
      // 'report' or 'safety' - the one dialog serves both.
      issueType: "report",
      issueForm: { issueType: "driver_behavior", description: "" },
      submittingIssue: false,
      issueTypeOptions: ISSUE_TYPES,
      // Scroll-arrow visibility, same as TripTimeLine on the standard details
      // page: left shows once scrolled away from the start, right while there
      // is still more to the right.
      isAtStart: false,
      isAtEnd: false,
      detachDrag: null,
    };
  },
  computed: {
    status() {
      return (this.trip && this.trip.uberStatus) || "";
    },
    driver() {
      return (this.trip && this.trip.driver) || {};
    },
    vehicle() {
      return (this.trip && this.trip.vehicle) || {};
    },
    driverDetailsVisible() {
      return shouldShowDriverDetails(this.status);
    },
    vehicleName() {
      const name = `${this.vehicle.make || ""} ${this.vehicle.model || ""}`.trim();
      return name || "-";
    },
    guestName() {
      const guest = (this.trip && this.trip.guest) || {};
      return `${guest.firstName || ""} ${guest.lastName || ""}`.trim() || "-";
    },
    tripTypeLabel() {
      const found = TRIP_TYPE_OPTIONS.find((option) => option.id === (this.trip && this.trip.tripType));
      return found ? found.name : "-";
    },
    serviceTypeLabel() {
      const type = this.trip && this.trip.serviceType;
      if (type === 1) return "Pickup";
      if (type === 2) return "Drop off";
      return "-";
    },
    /**
     * Oldest first, so the horizontal timeline reads left to right.
     *
     * Two sources, normalised to one shape. A booked trip shows its Uber history
     * (already merged across every attempt by the server). A trip with no
     * booking yet - To be Booked - falls back to the trip's own log entries, so
     * the drawer says something useful instead of "No status history yet".
     */
    timeline() {
      const uberHistory = (this.trip && this.trip.history) || [];

      if (uberHistory.length > 0) {
        return uberHistory.map((entry) => ({
          time: this.getTimeDate(entry.timestamp),
          status: statusLabel(entry.status),
          statusKey: entry.status,
          message: entry.message || "",
          employee: entry.employee || "Uber",
          superseded: entry.superseded === true,
          location: null,
        }));
      }

      // Already formatted by timeLineFilter on the server.
      return ((this.trip && this.trip.tripTimeline) || []).map((entry) => ({
        time: entry.date,
        status: entry.status,
        statusKey: null,
        message: "",
        employee: entry.employee || "-",
        superseded: false,
        location: entry.location || null,
      }));
    },

    /** True once the trip carries an Uber booking. */
    isBooked() {
      return !!(this.trip && this.trip.uberBooked);
    },

    /** Uber status for a booked trip, taSki trip status otherwise. */
    statusColumn() {
      if (this.isBooked) {
        return { label: "Uber Status", value: statusLabel(this.status), color: statusColor(this.status) };
      }
      const tripStatus = (this.trip && this.trip.tripStatus) || null;
      return { label: "Trip Status", value: (tripStatus && tripStatus.name) || "-", color: "#333333" };
    },
    cancellable() {
      return isCancellable(this.status);
    },
    callable() {
      return canCallDriver(this.status);
    },
    messageable() {
      return canMessageDriver(this.status);
    },
    reportable() {
      return canReportIssue(this.status);
    },
    /**
     * Whether the ride is still to happen. Drives the ETA fields and the
     * tracking button: both describe a car that is still coming, and Uber's
     * tracking link stops working once the trip ends.
     */
    ridePending() {
      return hasLiveTracking(this.status);
    },
    trackable() {
      return this.ridePending && !!(this.trip && this.trip.trackingUrl);
    },

    /**
     * The PIN the crew reads out to the driver at pickup.
     *
     * Uber's convention is the last four digits of the passenger's number, so it
     * is derived here rather than stored - the same four the crew's own ride
     * page shows.
     */
    ridePin() {
      const mobile = String((this.trip && this.trip.guest && this.trip.guest.phoneNumber) || "");
      return mobile.length >= 4 ? mobile.slice(-4) : "";
    },
    showPin() {
      return hasDriverAssigned(this.status) && !!this.ridePin;
    },

    /**
     * The shareable ride page, from the booking onwards.
     *
     * Offered from Scheduled through Completed: before a booking there is no
     * Uber trip id to key the link on, and afterwards the page still serves as
     * the crew's record of the ride.
     */
    shareable() {
      return !!(this.trip && this.trip.uberTripId);
    },
    userLink() {
      if (!this.shareable) return "";
      return `${window.location.origin}/uber/trip-view/${this.trip.uberTripId}`;
    },

    /**
     * Whether there is a receipt to offer.
     *
     * Follows the flag Uber sets rather than the trip status - see hasReceipt.
     * Still computed while the card is commented out, so restoring the card
     * needs no more than uncommenting it.
     */
    receiptable() {
      return hasReceipt(this.status, this.trip && this.trip.receiptStatus);
    },

    /**
     * The charge lines, rearranged into something a person would recognise.
     *
     * Uber's own order is arbitrary, and it puts tax entries in among the
     * charges under raw internal names - "transport.cgst.tax",
     * "transport.fare.platform_fees.tax.igst". Three of those stacked in a
     * receipt tell a reader nothing they can act on.
     *
     * So: the trip charge first, because that is the one being explained; then
     * the other real charges; then every tax added together on one Tax line.
     */
    receiptLines() {
      const receipt = this.receipt || {};
      const all = []
        .concat(receipt.charges || [])
        .concat(receipt.chargeAdjustments || [])
        .concat(receipt.taxes || []);

      const isTax = (line) => /(^|[.\s_])tax(es)?([.\s_]|$)|gst|vat/i.test(String(line.name || ""));
      /*
       * Which charge is the fare itself, and so goes first.
       *
       * Uber does not use one name for it - "Trip Charge" on one receipt,
       * "Suggested fare" on another - so matching a literal misses it as soon as
       * the wording changes, and the receipt then opens with a booking fee.
       *
       * Anything whose name ENDS in "fare" is the fare: "Suggested fare",
       * "Base fare", "Normal fare", "Ride fare". "Trip Charge" is kept as a
       * second test because it is the one common name that does not. Ending
       * rather than containing, so a "Fare adjustment" is not mistaken for it.
       */
      const isTripCharge = (line) => {
        const name = String(line.name || "");
        return /fare\s*$/i.test(name) || /trip\s*charge\s*$/i.test(name);
      };

      /*
       * Uber's own three lists mean different things, so they are kept apart.
       *
       * `charges` is what the ride cost; `charge_adjustments` is what was added
       * to it afterwards - waiting time, tolls, a cancellation fee. Flattened
       * together, "Wait Time" reads as part of the fare, which is the one thing
       * a passenger queries. A heading before the first adjustment says which is
       * which without needing a column for it.
       */
      const charges = (receipt.charges || []).filter((l) => !isTax(l));
      const adjustments = (receipt.chargeAdjustments || []).filter((l) => !isTax(l));
      const taxes = all.filter(isTax);

      /*
       * Surge sits with the fare, not with the adjustments.
       *
       * Uber defines subtotal as normal_fare + surge_charge.amount, so it is a
       * component of the fare rather than something added afterwards - listing
       * it under Other Charges alongside waiting time would misstate what it is.
       * Directly after the trip charge: the fare, then its uplift.
       *
       * It joins `lines` here, before the tax block below, because that block
       * sums these lines to decide whether tax is already inside the fare. A
       * surge left out of that sum makes a surged ride look as though its
       * charges fall short of the total, and the tax then never gets netted.
       */
      const surge = receipt.surgeCharge && receipt.surgeCharge.name ? [receipt.surgeCharge] : [];

      const lines = charges
        .filter(isTripCharge)
        .concat(surge)
        .concat(charges.filter((l) => !isTripCharge(l)))
        .concat(
            // The heading rides on the first one, so a receipt with no
            // adjustments never prints an empty section.
            adjustments.map((line, index) =>
                index === 0 ? { ...line, heading: "Other Charges" } : line
            )
        );

      if (taxes.length) {
        const taxTotal = taxes.reduce((sum, l) => sum + (Number(l.amount) || 0), 0);
        if (taxTotal > 0) {
          /*
           * Is the tax on top of the charges, or already inside them?
           *
           * Uber sends both kinds and labels neither. On a real Indian fare the
           * charges alone summed to the stated total exactly, the cgst/sgst/igst
           * rows being the component *within* that - so listing them as extra
           * lines made the column read 605.12 against a total of 577.59.
           *
           * Both forms of Uber's total are its own total_fare - the string in
           * totalAmount, the number in totalFareAmount. Neither comes from
           * home_currency_total_fare, which is converted into the organisation's
           * billing currency and so disagrees with rupee charges even when they
           * agree exactly. That block is no longer read at all.
           */
          const charged = lines.reduce((sum, l) => sum + (Number(l.amount) || 0), 0);
          const stated = this.amountOf(receipt.totalAmount, receipt.totalFareAmount);
          const included = Number.isFinite(stated) && Math.abs(charged - stated) < 0.01;

          /*
           * When it is inside, take it back out of the fare and list it.
           *
           * Showing "Tax (included)" was honest but left a reader doing the
           * subtraction themselves to see what the ride actually cost. Netting
           * it out of the trip charge means every line is an addition and the
           * column reaches the printed total on its own - which is the only
           * arrangement nobody has to be told how to read.
           */
          if (included) {
            const netIndex = lines.findIndex(isTripCharge);
            // Falls back to the largest line when Uber names the fare something
            // else, so the column still reconciles.
            const target = netIndex >= 0
              ? netIndex
              : lines.reduce((best, l, i, all) => (Number(l.amount) > Number(all[best].amount) ? i : best), 0);

            if (lines[target]) {
              lines[target] = { ...lines[target], amount: Number(lines[target].amount) - taxTotal };
            }
          }

          // Each tax under its own name and rate - SGST and CGST are different
          // taxes to whoever reconciles this, and "Tax 27.53" hides both which
          // applied and at what rate.
          taxes.forEach((line, index) => {
            lines.push({
              name: this.taxLineName(line),
              amount: line.amount,
              ...(index === 0 ? { heading: "Taxes", spaced: true } : {}),
            });
          });
        }
      }

      return lines;
    },
    /**
     * Uber sends the total already formatted, symbol and all - printed as it
     * arrives. The numeric copy is only there for anything that has to add up.
     */
    receiptTotal() {
      const receipt = this.receipt || {};
      // totalAmount holds Uber's own formatted string, symbol and all, so it is
      // printed as it arrives. totalFareAmount is the same figure as a number,
      // used only when there is no string to show.
      if (receipt.totalAmount) return receipt.totalAmount;
      if (receipt.totalFareAmount !== undefined && receipt.totalFareAmount !== null) {
        return `${receipt.currencyCode || "INR"} ${Number(receipt.totalFareAmount).toFixed(2)}`;
      }
      return "-";
    },
    /**
     * The ride's currency symbol, taken off Uber's own formatted total.
     *
     * NOT from currencyCode, which reports the organisation's billing currency -
     * it said USD on a fare charged in rupees. total_fare, stored here as
     * totalAmount, is the one figure Uber gives in the money actually charged,
     * so its symbol is the one to repeat on the lines.
     */
    currencySymbol() {
      const formatted = String((this.receipt && this.receipt.totalAmount) || "");
      const match = formatted.match(/^[^\d.,\s-]+/);
      return match ? match[0] : "";
    },

    /**
     * The same currency, in something the PDF can draw.
     *
     * jsPDF's built-in fonts are WinAnsi-encoded and have no rupee glyph, so the
     * symbol is swapped for its ISO code rather than silently dropped. Mapped
     * from the symbol, not from currencyCode, for the reason above.
     */
    pdfCurrency() {
      const symbol = this.currencySymbol;
      if (!symbol) return "";
      // eslint-disable-next-line no-control-regex
      if (/^[\x20-\x7E]+$/.test(symbol)) return symbol;
      return { "₹": "INR ", "€": "EUR ", "£": "GBP ", "¥": "JPY " }[symbol] || "";
    },

    /**
     * The settled total, for the details card.
     *
     * Read off finalFare - the receipt - rather than off `fare`, which comes
     * from the trip refresh and is an estimate until the ride ends. Empty
     * string when there is nothing settled yet, so the column simply does not
     * appear rather than showing a dash.
     */
    finalFareTotal() {
      const settled = (this.trip && this.trip.finalFare) || {};
      if (settled.totalAmount) return settled.totalAmount;
      if (settled.totalFareAmount !== undefined && settled.totalFareAmount !== null) {
        return `${settled.currencyCode || "INR"} ${Number(settled.totalFareAmount).toFixed(2)}`;
      }
      return "";
    },
    /**
     * A charge on a cancelled ride is a cancellation fee, not a fare, and
     * calling it one invites the question of why a trip that never ran cost
     * anything.
     */
    finalFareLabel() {
      const group = statusDescriptor(this.status).group;
      return group === STATUS_GROUP.CANCELLED ? "Cancellation Fee" : "Total Fare";
    },

    /** When the ride happened, for the receipt's dateline. */
    receiptDate() {
      const when = this.trip && (this.trip.pickupTime || this.trip.createdAt);
      return when ? moment(when).format("DD MMM YYYY") : "";
    },
    pickupText() {
      const pickup = (this.trip && this.trip.pickup) || {};
      return pickup.address || "";
    },
    dropText() {
      const dropoff = (this.trip && this.trip.dropoff) || {};
      return dropoff.address || "";
    },
    driverLine() {
      if (!this.driverDetailsVisible || !this.driver.name) return "";
      const car = this.vehicleName !== "-" ? this.vehicleName : "";
      const plate = this.vehicle.licensePlate || this.vehicle.vehicleno || "";
      return [this.driver.name, [plate, car].filter(Boolean).join(" ")]
        .filter(Boolean)
        .join(" · ");
    },
    /**
     * The trip details block, shared by the dialog and the PDF so the two can
     * never drift apart. Only rows that have something in them.
     */
    receiptFacts() {
      const trip = this.trip || {};
      // Distance and duration are deliberately absent: Uber reports them for the
      // metered portion, so they read as 0.01 km / 00:05:39 on a ride that
      // plainly went further, and an unexplainable number on a receipt invites
      // exactly the query the receipt was meant to answer.
      // No mobile and no driver: a receipt is a record of what was charged, and
      // neither number nor name explains a figure on it. The mobile in
      // particular is personal data that would then live in every saved PDF.
      const receipt = this.receipt || {};
      return [
        { key: "Passenger", value: this.guestName },
        // Uber's expense_memo - why the trip was taken. It states this appears
        // on the receipt, so it belongs here rather than only in storage.
        { key: "Purpose", value: receipt.expenseMemo || "" },
        { key: "Pickup", value: this.pickupText },
        { key: "Drop", value: this.dropText },
        // Both references, because they are quoted to different people: ours to
        // the office, Uber's to Uber support.
        { key: "Trip ID", value: String(trip.displayId || trip.tripId || "") },
        { key: "Uber trip", value: trip.uberTripId || "" },
      ].filter((fact) => fact.value && fact.value !== "-");
    },

    /**
     * What Uber finally charged. Only present once the ride is over and the
     * refresh has picked the figure up, so the column is conditional.
     *
     * Commented out with the Fare column it feeds - see customer-details-modal
     * above. Nothing else reads these two.
     */
    // fareShown() {
    //   const fare = (this.trip && this.trip.fare) || {};
    //   return fare.final !== undefined && fare.final !== null && fare.final !== "";
    // },
    // fareText() {
    //   const fare = (this.trip && this.trip.fare) || {};
    //   return `${fare.currencyCode || "INR"} ${fare.final}`;
    // },

    hasActions() {
      return (
        // Left out while the User Link card is commented out above - counting it
        // here would draw an empty Actions panel on a trip that has no other
        // action available.
        // this.shareable ||
        this.receiptable ||
        this.trackable ||
        this.callable ||
        this.messageable ||
        this.reportable ||
        this.cancellable
      );
    },
    canSubmitIssue() {
      if (this.submittingIssue) return false;
      if (!this.issueForm.description || !this.issueForm.description.trim()) return false;
      return this.issueType === "safety" || !!this.issueForm.issueType;
    },
  },
  watch: {
    tripId: {
      immediate: true,
      handler() {
        this.getTripDetails();
      },
    },
  },
  mounted() {
    this.setupDragScroll();
    this.unsubscribe = uberRealtime.subscribe({
      update: (update) => {
        // Only refresh when the open trip is the one that moved.
        if (this.trip && update.tripId === this.trip.tripId) this.getTripDetails();
      },
    });
  },
  beforeDestroy() {
    if (this.unsubscribe) this.unsubscribe();
    if (this.detachDrag) this.detachDrag();
  },
  methods: {
    statusLabel,
    statusColor,

    /* ---------------------------------------------------------------- *
     * Timeline scrolling - drag to pan plus arrow buttons, same as
     * TripTimeLine on the standard details page.
     * ---------------------------------------------------------------- */

    setupDragScroll() {
      const container = this.$refs.timelineContainer;
      if (!container) return;

      let isDragging = false;
      let startX = 0;
      let scrollStart = 0;

      const onDown = (e) => {
        isDragging = true;
        container.classList.add("dragging");
        startX = e.pageX - container.offsetLeft;
        scrollStart = container.scrollLeft;
      };
      const onUp = () => {
        isDragging = false;
        container.classList.remove("dragging");
      };
      const onMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.pageX - container.offsetLeft;
        container.scrollLeft = scrollStart - (x - startX) * 2;
        this.showScroll();
      };

      container.addEventListener("mousedown", onDown);
      container.addEventListener("mouseleave", onUp);
      container.addEventListener("mouseup", onUp);
      container.addEventListener("mousemove", onMove);

      // Returned so beforeDestroy can detach - these are on a raw DOM node, so
      // Vue will not clean them up on its own.
      this.detachDrag = () => {
        container.removeEventListener("mousedown", onDown);
        container.removeEventListener("mouseleave", onUp);
        container.removeEventListener("mouseup", onUp);
        container.removeEventListener("mousemove", onMove);
      };
    },

    /**
     * Parks the timeline on the newest entry.
     *
     * History is oldest-first, so the latest event sits at the far right - and
     * on a re-booked trip there can be seven or more entries, which would
     * otherwise open showing the original booking rather than what just
     * happened. Mirrors the same jump TripTimeLine does after it loads.
     */
    scrollToLatest() {
      const container = this.$refs.timelineContainer;
      if (!container) return;
      container.scrollLeft = container.scrollWidth;
      this.showScroll();
    },

    smoothScroll(container, direction, amount, duration) {
      const start = container.scrollLeft;
      const change = direction === "right" ? amount : -amount;
      const startTime = performance.now();

      const step = (now) => {
        const progress = Math.min((now - startTime) / duration, 1);
        container.scrollLeft = start + change * progress;
        if (progress < 1) requestAnimationFrame(step);
        else this.showScroll();
      };
      requestAnimationFrame(step);
    },

    scrollLeft() {
      const container = this.$refs.timelineContainer;
      if (container) this.smoothScroll(container, "left", 400, 300);
    },

    scrollRight() {
      const container = this.$refs.timelineContainer;
      if (container) this.smoothScroll(container, "right", 400, 300);
    },

    showScroll() {
      const container = this.$refs.timelineContainer;
      if (!container) return;
      this.isAtStart = container.scrollLeft > 10;
      this.isAtEnd = container.scrollLeft + container.clientWidth < container.scrollWidth - 10;
    },

    /**
     * The backend joins the two sources: Trip carries the route and booking
     * summary, UberTrip carries the history timeline and tracking link.
     */
    async getTripDetails() {
      if (!this.tripId) {
        this.trip = null;
        return;
      }
      this.loading = true;
      try {
        const response = await axios.post("uber/trip/details", { tripId: this.tripId });
        this.trip = response.data && Object.keys(response.data).length > 0 ? response.data : null;
        // Jump to the newest entry, the way TripTimeLine does after it loads.
        // Only measurable once the entries are in the DOM.
        this.$nextTick(this.scrollToLatest);
      } catch (e) {
        this.trip = null;
      } finally {
        this.loading = false;
      }
    },

    async cancelTrip() {
      // "Yes" rather than a named button: next to Cancel, a "Cancel Trip"
      // button in a cancellation dialog reads as two ways to back out.
      const confirmed = await this.$refs.confirm.open(
        "Confirm Uber Ride Cancellation",
        "Are you sure you want to cancel this Uber trip?",
        { icon: "fas fa-times-circle", color: "#000000" }
      );
      if (!confirmed) return;

      this.cancelling = true;
      try {
        const response = await axios.post("uber/trip/cancel", { tripId: this.trip.tripId });
        toast.success((response.data && response.data.message) || "Trip cancelled.");
        this.$emit("cancelled", this.trip.tripId);
        this.getTripDetails();
      } catch (err) {
        this.toastError(err, "Failed to cancel the trip.");
      } finally {
        this.cancelling = false;
      }
    },

    /**
     * Asks Uber to ring the operator and bridge them to the driver.
     *
     * The operator's own phone rings, which is worth saying up front - it is not
     * what "call driver" usually implies.
     */
    async callDriver() {
      const confirmed = await this.$refs.confirm.open(
        "Call Driver",
        "Uber will call you first, then connect you to the driver. Continue?",
        { icon: "fas fa-phone", color: "#d59e1d", buttonName: "Call" }
      );
      if (!confirmed) return;

      this.calling = true;
      try {
        const response = await axios.post("uber/trip/call", { tripId: this.trip.tripId });
        toast.success((response.data && response.data.message) || "Calling you now.");
        // The call is logged against the booking, so the timeline changes.
        this.getTripDetails();
      } catch (err) {
        this.toastError(err, "Could not place the call.");
      } finally {
        this.calling = false;
      }
    },

    openUserLink() {
      if (this.userLink) window.open(this.userLink, "_blank", "noopener,noreferrer");
    },

    async copyUserLink() {
      if (!this.userLink) return;
      try {
        await navigator.clipboard.writeText(this.userLink);
        this.linkCopied = true;
        setTimeout(() => {
          this.linkCopied = false;
        }, 2000);
      } catch (err) {
        // Clipboard access needs a secure context, which a plain-http staging
        // host is not. Open the page instead, so the link can be taken from the
        // address bar rather than being lost.
        toast.error("Could not copy - opening the link instead.");
        this.openUserLink();
      }
    },

    /** Uber's own tracking page - the same view the crew is sent. */
    openTracking() {
      if (this.trip && this.trip.trackingUrl) {
        window.open(this.trip.trackingUrl, "_blank", "noopener,noreferrer");
      }
    },

    /**
     * Shows what Uber charged for the ride.
     *
     * Uber's receipt endpoint answers with JSON, not a document - there is no
     * file to save and no page to open - so the server fetches the figures and
     * this renders them. Once a receipt has been taken it is stored against the
     * trip, and the dialog opens from that without another round trip.
     */
    async openReceipt() {
      if (!this.trip) return;

      // Normally already here: the receipt_ready webhook fetches the figures the
      // moment Uber issues them, so /trip/details brings them with everything
      // else and this opens without a round trip.
      if (this.trip.finalFare) {
        this.receipt = this.trip.finalFare;
        this.receiptDialog = true;
        return;
      }

      this.loadingReceipt = true;
      try {
        const response = await axios.post("uber/trip/receipt", { tripId: this.trip.tripId });
        this.receipt = (response.data && response.data.finalFare) || null;
        this.receiptDialog = true;
        // The first open writes the receipt and a timeline entry, so the drawer
        // is out of date until this returns.
        this.getTripDetails();
      } catch (err) {
        this.toastError(err, "Could not open the receipt.");
      } finally {
        this.loadingReceipt = false;
      }
    },

    /**
     * Saves the receipt as a PDF, drawn here rather than fetched.
     *
     * Uber has no document to give, so one is composed - jsPDF draws it in the
     * browser, which keeps a PDF engine and its Chromium off the API server.
     *
     * Everything written here goes through asciiText(): jsPDF's built-in fonts
     * are WinAnsi-encoded and have no rupee glyph, so Uber's own "Rs.284.59"
     * string would come out mangled. Amounts are rebuilt from the numeric copy
     * and the currency code instead, and any remaining non-Latin character is
     * dropped rather than drawn as a box.
     */
    downloadReceipt() {
      if (!this.receipt) return;

      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const LEFT = 56;
      const RIGHT = 539;
      let y = 64;

      const text = (value, options) => {
        const opts = options || {};
        doc.setFont("helvetica", opts.bold ? "bold" : "normal");
        doc.setFontSize(opts.size || 10);
        doc.setTextColor(opts.color || "#1e293b");
        doc.text(this.asciiText(value), opts.x === undefined ? LEFT : opts.x, y, {
          align: opts.align || "left",
        });
      };
      const rule = () => {
        doc.setDrawColor("#e2e8f0");
        doc.setLineWidth(0.7);
        doc.line(LEFT, y, RIGHT, y);
      };

      /*
       * The same receipt that is on screen, not a plain-text rendering of it.
       *
       * The dialog leads with a dark band carrying the total, so the page does
       * too - a saved receipt and the one it was saved from should be
       * recognisably the same document.
       */
      const HERO_H = 92;
      doc.setFillColor("#374151");
      doc.rect(0, 0, 595, HERO_H, "F");

      y = 34;
      text("TOTAL CHARGED", { size: 8, color: "#cbd5e1" });
      y += 26;
      text(this.pdfAmount(this.receipt.totalFareAmount, this.receipt.totalAmount), {
        bold: true,
        size: 22,
        color: "#ffffff",
      });
      y += 18;
      const when = this.receiptDate;
      text(`Trip ${this.trip.displayId || this.trip.tripId}${when ? "  |  " + when : ""}`, {
        size: 9,
        color: "#cbd5e1",
      });

      y = HERO_H + 38;

      if (this.receiptLines.length) {
        this.receiptLines.forEach((line) => {
          // The same "Other Charges" heading the dialog shows, so an adjustment
          // is not mistaken for part of the fare here either.
          if (line.heading) {
            text(line.heading.toUpperCase(), { size: 8, color: "#94a3b8" });
            y += 16;
          }
          // The same gap the dialog leaves above the tax.
          if (line.spaced) y += 8;
          text(line.name, { size: 10 });
          text(this.pdfLineAmount(line.amount), { size: 10, x: RIGHT, align: "right" });
          y += 17;
        });
        y += 3;
        rule();
        y += 17;
        text("Total", { bold: true, size: 11 });
        text(this.pdfAmount(this.receipt.totalFareAmount, this.receipt.totalAmount), {
          bold: true,
          size: 11,
          x: RIGHT,
          align: "right",
        });
        y += 32;
      }

      text("TRIP DETAILS", { size: 8, color: "#64748b" });
      y += 17;
      this.receiptFacts.forEach((fact) => {
        text(fact.key, { size: 9.5, color: "#64748b" });
        // Wrapped, because an address will not fit on one line.
        const lines = doc.splitTextToSize(this.asciiText(fact.value), RIGHT - LEFT - 96);
        lines.forEach((line, index) => {
          if (index > 0) y += 14;
          text(line, { size: 9.5, x: LEFT + 96 });
        });
        y += 18;
      });

      y += 12;
      rule();
      y += 15;
      text("Booked through taSki. Fare charged by Uber.", { size: 8, color: "#94a3b8" });

      doc.save(`uber-receipt-${this.trip.displayId || this.trip.tripId}.pdf`);
    },

    /**
     * An amount in a form the PDF's fonts can actually draw.
     *
     * Built from the numeric value and the currency code, never from Uber's
     * pre-formatted string, which carries a rupee sign the built-in fonts have
     * no glyph for. The string is only a last resort, stripped to ASCII.
     */
    /** A charge line for the PDF: the ISO code, since the symbol cannot be drawn. */
    pdfLineAmount(amount) {
      if (amount === undefined || amount === null || amount === "") return "-";
      const value = Number(amount);
      const figure = Number.isFinite(value) ? value.toFixed(2) : String(amount);
      return `${this.pdfCurrency}${figure}`;
    },

    pdfAmount(amount, formatted) {
      /*
       * No currency code on the figures.
       *
       * currencyCode cannot be trusted: Uber has returned "USD" on a ride
       * plainly charged in rupees, while total_fare on the same payload said
       * "Rs.577.59". Printing the code beside the number would state something
       * false; printing the number alone states less but nothing wrong.
       *
       * Uber's own formatted total is preferred where there is one. Its symbol
       * is dropped by asciiText because the PDF's built-in fonts have no glyph
       * for it, which leaves the bare number - the same thing, minus a symbol.
       */
      // Rebuilt from the number rather than passed through, so the code is
      // prefixed exactly once - stripping the symbol and then prefixing the
      // string would double it up on a currency whose symbol IS ASCII.
      const value = this.amountOf(formatted, amount);
      if (Number.isFinite(value)) return `${this.pdfCurrency}${value.toFixed(2)}`;
      return formatted ? this.asciiText(formatted) : "-";
    },

    /** Drops anything the PDF's WinAnsi fonts cannot render. */
    asciiText(value) {
      return String(value === undefined || value === null ? "" : value)
        // eslint-disable-next-line no-control-regex
        .replace(/[^\x20-\x7E]/g, "")
        .trim();
    },

    /**
     * A charge line's amount.
     *
     * These are bare numbers, unlike the total, which Uber sends already
     * formatted with its currency symbol - so only these get a currency put in
     * front of them. Collapsing the two would either strip the symbol off the
     * total or print it twice.
     */
    /**
     * A number out of Uber's formatted total, e.g. "₹577.59" -> 577.59.
     *
     * Needed because the only figure Uber gives in the ride's own currency is
     * that display string; the numeric copy beside it is converted to the
     * organisation's home currency and cannot be compared with the charges.
     */
    amountOf(formatted, fallback) {
      const digits = String(formatted || "").replace(/[^\d.-]/g, "");
      const value = Number(digits);
      if (digits !== "" && Number.isFinite(value)) return value;
      const alternative = Number(fallback);
      return Number.isFinite(alternative) ? alternative : NaN;
    },

    /**
     * The tax's real name, out of Uber's internal one.
     *
     * "transport.cgst.tax" and "transport.fare.platform_fees.tax.igst" mean CGST
     * and IGST to anyone reconciling this, and nothing at all as written. The
     * four-letter forms are tested before plain GST, or "igst" would match the
     * shorter one and every tax would read the same.
     */
    /**
     * How a tax line reads: the tax's name, then its rate in brackets.
     *
     * `rateType` is the name Uber gives the tax, so it is preferred over the
     * internal one on `name` ("transport.cgst.tax"), which only ever needed
     * unpicking because nothing better was there. Either way it goes through
     * taxLabel, so an acronym comes out as CGST rather than cgst.
     *
     * Uber names alternatives as a pair - "SGST/UTGST" - where only one of them
     * actually applied, so the receipt shows the first and drops the rest. The
     * slash otherwise reads as though both were charged.
     *
     * The name alone: the rate is still stored on the line and in finalFare, it
     * is simply not printed, because the amount is what a receipt is read for.
     */
    taxLineName(line) {
      const entry = line || {};
      const type = entry.rateType || entry.rate_type;
      const primary = String(type || "").split("/")[0].trim();
      return this.taxLabel(primary || entry.name);
    },

    taxLabel(name) {
      const raw = String(name || "");
      const specific = raw.match(/(^|[._\s])(cgst|sgst|igst|ugst)([._\s]|$)/i);
      if (specific) return specific[2].toUpperCase();

      const general = raw.match(/(^|[._\s])(gst|vat)([._\s]|$)/i);
      if (general) return general[2].toUpperCase();

      return raw ? raw : "Tax";
    },

    lineAmount(amount) {
      if (amount === undefined || amount === null || amount === "") return "-";
      const value = Number(amount);
      const figure = Number.isFinite(value) ? value.toFixed(2) : String(amount);
      return `${this.currencySymbol}${figure}`;
    },

    openIssue(type) {
      this.issueType = type;
      this.issueForm = { issueType: "driver_behavior", description: "" };
      this.issueDialog = true;
    },

    async submitIssue() {
      if (!this.canSubmitIssue) return;

      const safety = this.issueType === "safety";
      const payload = safety
        ? { tripId: this.trip.tripId, description: this.issueForm.description.trim() }
        : {
            tripId: this.trip.tripId,
            issueType: this.issueForm.issueType,
            description: this.issueForm.description.trim(),
          };

      this.submittingIssue = true;
      try {
        const response = await axios.post(
          safety ? "uber/trip/safety-concern" : "uber/trip/report-issue",
          payload
        );
        toast.success((response.data && response.data.message) || "Recorded.");
        this.issueDialog = false;
        // Both write a history entry, so the timeline shows the report.
        this.getTripDetails();
      } catch (err) {
        this.toastError(err, "Could not record that.");
      } finally {
        this.submittingIssue = false;
      }
    },

    /** The encrypted responses come back as an object or a bare string. */
    toastError(err, fallback) {
      toast.error(readApiError(err, fallback));
    },

    close() {
      this.$emit("closeTripDetails");
    },

    addressOf(place) {
      if (!place) return "-";
      return place.address || "-";
    },


    /** Timestamps arrive as epoch values or ISO strings. */
    getTimeDate(data) {
      if (data === null || data === undefined || data === "") return "-";
      const parsed =
        typeof data === "number" || /^\d+$/.test(String(data)) ? moment(Number(data)) : moment(data);
      return parsed.isValid() ? parsed.format("DD-MM-YYYY HH:mm") : "-";
    },
  },
};
</script>

<style scoped lang="scss">
// Same card styling as the standard trip details page.
@import "@/components/Trips/trip-details/tripDetails";

// The imported grid uses fixed percentages that must total 100% per row.
// Adding Reporting Time splits the 30% Trip Date column in two, keeping
// row two at 15 + 15 + 35 + 35.
.trip-details-modal {
  .details-column.date-column {
    width: 15%;
  }

  .details-column.reporting-column {
    width: 15%;
    border-top: none;
  }
}

.close-blk {
  position: absolute;
  right: 20px;
  top: 14px;
  cursor: pointer;
  z-index: 3;
  color: #5a5a5a;

  &:hover {
    color: #000000;
  }

  svg {
    height: 28px;
    width: 28px;
  }
}

.uber-details-outer {
  flex: 1;
  position: relative;
  background: #ffffff;
  border-radius: 5px;
  box-shadow: 0px 0px 5px 0px rgba(106, 7, 75, 0.15);
  overflow-y: auto;
  padding: 0 16px 16px;
}

.empty-state {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #b0b0b0;
  font-size: 14px;

  i {
    font-size: 34px;
  }
}

/* Timeline - same treatment as TripTimeLine on the standard details page. */
.timeline-container {
  margin-bottom: 20px;
  width: 100%;
  overflow-x: auto;
  white-space: nowrap;
  position: relative;
  top: 20px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  height: auto;
  border: 1px solid #ddd;
  padding: 0 20px;
  box-sizing: border-box;
  box-shadow: 0px 0px 10px 0px rgba(106, 7, 75, 0.2);
  border-radius: 5px;
  user-select: none;
  cursor: grab;

  &.dragging {
    cursor: grabbing;
  }

  &::-webkit-scrollbar {
    display: none;
  }
}

.timeline-content {
  display: flex;
  align-items: flex-start;
  position: relative;
  padding: 20px 0;
}

.timeline-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  word-wrap: break-word;
  position: relative;
  // No min-width and no white-space reset below: each entry sizes to its own
  // content and inherits nowrap from the container, so long values like the
  // partner name stay on one line instead of wrapping into a cramped column.
  padding: 0 10px;

  &:after {
    content: "";
    position: absolute;
    top: 7px;
    width: 100%;
    height: 2px;
    background-color: #ddd;
    z-index: 1;
  }

  &:last-child:after {
    display: none;
  }
}

.timeline-dot {
  width: 15px;
  height: 15px;
  background-color: #000;
  border-radius: 50%;
  position: relative;
  z-index: 2;

  &.current {
    background-color: #d59e1d;
    box-shadow: 0 0 0 3px rgba(213, 158, 29, 0.25);
  }

  // Entries from an attempt that was superseded by a re-book.
  &.superseded {
    background-color: #c9c9c9;
  }
}

// Arrow buttons sit outside the scroller, same treatment as TripTimeLine.
.timeline-outer {
  position: relative;
  display: flex;
  align-items: center;
}

.scroll-btn {
  background: #fa7f22;
  color: white;
  border: none;
  padding: 10px;
  cursor: pointer;
  z-index: 10;
  position: absolute;
  border-radius: 20px;
  opacity: 0.4;

  &:hover {
    opacity: 1;
  }
}

.left-btn {
  left: 4px;
}

.right-btn {
  right: 4px;
}

.timeline-label {
  margin-top: 10px;
  font-size: 14px;
  color: #555;
  text-align: center;
}

.timeline-date {
  font-weight: bold;
  font-size: 12px;
  color: #000;
}

// The status is what ops scans the timeline for, so it carries the weight.
.timeline-status {
  font-size: 13px;
  font-weight: 700;
  color: #1a1a1a;
  margin-top: 5px;
}

.timeline-action {
  font-size: 12px;
  color: #666;
  margin-top: 5px;
  word-break: break-word;
}

.timeline-by {
  font-size: 11px;
  font-weight: 600;
  color: #fa7815;
  margin-top: 4px;
  word-break: break-word;
}

.timeline-empty {
  padding: 24px 0;
  font-size: 13px;
  color: #9e9e9e;
}

// Read aloud to a driver, so it wants to be legible at a glance.
.ride-pin {
  font-size: 15px;
  letter-spacing: 3px;
}

/* ---------------------------------------------------------------------- *
 * Action panel - same shape as the corporate portal's Agent Support cards
 * ---------------------------------------------------------------------- */
.action-panel {
  margin-top: 14px;
  padding: 16px 18px 18px;
  border-radius: 6px;
}

.action-panel-title {
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  letter-spacing: -0.2px;
  margin-bottom: 12px;
}

.action-grid {
  display: grid;
  // Cards size themselves; the drawer is wide but the panel must still hold up
  // at narrower widths.
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 12px;
}

.action-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 14px 14px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}

.action-card-title {
  margin-top: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  letter-spacing: -0.2px;
}

// Says what the action does before it is pressed - the point of the panel.
.action-card-sub {
  margin-top: 3px;
  font-size: 11.5px;
  line-height: 1.4;
  color: #64748b;
}

.action-card-btn {
  margin-top: 10px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
  border-radius: 8px;
}

// Open + Copy sit together; the card is narrow, so let them wrap.
.action-card-pair {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

/* ---------------------------------------------------------------------- *
 * Issue / safety dialog
 * ---------------------------------------------------------------------- */
.issue-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 18px;
  font-size: 15px;
  font-weight: 600;
  color: #ffffff;
  background: #b45309;

  &.safety {
    background: #b91c1c;
  }

  /* Slate, matching the Trip Receipt action card it opens from. */
  &.receipt {
    background: #374151;
  }
}

.issue-body {
  padding: 16px 18px 6px;
}

/* ---------------------------------------------------------------------- *
 * Receipt dialog - its own header, then the issue dialog's action row.
 * ---------------------------------------------------------------------- */
.receipt-hero {
  padding: 20px 22px 18px;
  background: #374151;
  color: #ffffff;
}

.receipt-hero-label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.65);
}

.receipt-hero-total {
  margin-top: 4px;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.6px;
  line-height: 1.1;
}

.receipt-hero-sub {
  margin-top: 6px;
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.7);
}

.receipt-body {
  padding: 18px 22px 4px;
}

.receipt-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  color: #1e293b;

  td {
    padding: 5px 0;
  }
}

.receipt-amount {
  text-align: right;
  white-space: nowrap;
}

/* Lifts the tax clear of the Other Charges list above it, so it reads as its
   own thing rather than one more adjustment. */
.receipt-spaced-row td {
  padding-top: 14px;
}

.receipt-group-row td {
  padding-top: 12px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  color: #94a3b8;
}

.receipt-total-row td {
  border-top: 1px solid #e2e8f0;
  padding-top: 9px;
  font-weight: 700;
  font-size: 14px;
}

.receipt-empty {
  font-size: 12px;
  color: #6a6a6a;
  margin-bottom: 4px;
}

.receipt-section {
  margin: 20px 0 8px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  color: #94a3b8;
}

.receipt-facts {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
  color: #1e293b;

  td {
    padding: 4px 0;
    vertical-align: top;
  }
}

.receipt-fact-key {
  width: 92px;
  color: #64748b;
  white-space: nowrap;
}

/* Breaks anywhere: a UUID has nothing to wrap on and would push the
   dialog wide otherwise. */
.receipt-uber-id {
  word-break: break-all;
  font-size: 11.5px;
  color: #64748b;
}

.issue-note {
  font-size: 12px;
  color: #6a6a6a;
  margin-bottom: 14px;
}

.issue-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 18px 16px;
}

.issue-submit {
  color: #ffffff !important;
  font-weight: 600;
}
</style>
